"use client";

import React, { useEffect, useState } from "react";
import * as z from "zod";
import { RegisterSchema } from "@/schemas";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import styles from "./register-form.module.scss";
import StyledInput from "@/components/common/input/styled-input";
import SignButton from "@/components/pages/auth/components/sign-button";
import GoogleButton from "@/components/pages/auth/components/google-button";
import HorizontalSpan from "@/components/pages/auth/components/horizontal-span";
import { register } from "@/actions/auth/register";
import TransparentButton from "@/components/common/transparent-button";
import Link from "next/link";
import ErrorText from "@/components/pages/auth/components/error-text";
import SuccessText from "@/components/pages/auth/components/success-text";
import BackHomeButton from "@/components/pages/auth/components/back-home-button";
import { useMobile } from "@/hooks/use-responsive";
import Image from "next/image";
import logo from "@/assets/sidebar/logo.svg";
import { useSearchParams } from "next/navigation";
import { showVerificationEmailSent } from "@/utils/notification-helper";
import { ToastContainer } from "react-toastify";
import FilledButton from "@/components/common/filled-button";
import DiscordButton from "@/components/pages/auth/components/discord-button";

const RegisterForm = () => {
  const { isMobile } = useMobile();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");

  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [success, setSuccess] = useState("");
  const [isPending, setIsPending] = useState(false);

  const form = useForm<z.infer<typeof RegisterSchema>>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof RegisterSchema>) => {
    setError("");
    setSuccess("");
    setIsPending(true);

    try {
      const data = await register(values, callbackUrl);

      if (data?.error) {
        form.reset();
        setError(data.error);
      }

      if (data?.success) {
        form.reset();
        setSuccess(data.success);
        showVerificationEmailSent(data.success);
      }
    } catch (error) {
      setError("Something went wrong");
    } finally {
      setIsPending(false);
    }
  };
  const onInvalid = (errors: any) => {
    setEmailError(errors.email?.message);
    setPasswordError(errors.password?.message);
  };

  useEffect(() => {
    const errorType = searchParams.get("error");
    if (errorType) {
      if (errorType == "OAuthAccountNotLinked")
        setError("Another account already exists with the same e-mail address");
    }
  }, [searchParams]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      form.handleSubmit(onSubmit, onInvalid)();
    }
  };

  return (
    <div className={styles.wrapper} onKeyDown={handleKeyDown}>
      <div className={styles.container}>
        <div className={styles.header_text_container}>
          Sign up
          <p className={styles.header_text_subtext}> for free</p>
        </div>

        <div className={styles.column}>
          <GoogleButton
            title="Sign up with Google"
            callbackUrl={callbackUrl}
            isDisabled={isPending}
          />
          <DiscordButton
            title="Sign up with Discord"
            callbackUrl={callbackUrl}
            isDisabled={isPending}
          />
          <HorizontalSpan />
          <div className={styles.inputs_column}>
            <div className={styles.input_container}>
              <div className={styles.input_box}>
                <p className={styles.input_title}>Enter your email</p>
                <StyledInput
                  type="email"
                  placeholder="name@domain.com"
                  isDisabled={isPending}
                  isValid={!emailError}
                  registerField={form.register("email")}
                  titleColor={"#F2F2F2"}
                />
              </div>
              <ErrorText error={emailError} />
            </div>
            <div className={styles.input_container}>
              <div className={styles.input_box}>
                <p className={styles.input_title}>Enter your password</p>
                <StyledInput
                  type="password"
                  placeholder="Password"
                  isDisabled={isPending}
                  isValid={!passwordError}
                  registerField={form.register("password")}
                  titleColor={"#F2F2F2"}
                />
              </div>
              <ErrorText error={passwordError} />
            </div>
            <FilledButton
              text={"Sign Up"}
              disabled={isPending}
              onClick={() => {
                setEmailError("");
                setPasswordError("");
                setError("");
                form.handleSubmit(onSubmit, onInvalid)();
              }}
              textTransform={"uppercase"}
              backgroundColor={"#FF5722"}
            />
            {error && <ErrorText error={error} />}
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.already_have_account}>
            Already have an AudioModify account?
            <Link className={styles.link_button} href={"/login"}>
              Log In
            </Link>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default RegisterForm;
